import { useDebouncedValue, useLocalStorage } from "@mantine/hooks"
import {
  Circle,
  CurrencyInr,
  DotsThreeVertical,
  Handshake,
  Info,
  List,
  MagnifyingGlass,
  Receipt,
  TrendDown,
} from "@phosphor-icons/react"
import {
  Avatar,
  CodenameTag,
  InputGroup,
  Menu,
  MenuItem,
  Popover,
  SegmentedControl,
  Tooltip,
} from "@vesatogo/grass-core"
import { Cell, TableView } from "@vesatogo/grass-table"
import { inr } from "@vesatogo/utils"
import classNames from "classnames"
import dayjs from "dayjs"
import { pick } from "lodash-es"
import { useEffect, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { useAuthorization } from "~/components/AuthorizationProvider"
import { RemarkModal } from "~/components/Remark/RemarkModal"
import { TableSummary } from "~/components/TableSummary"
import TradeKindTag from "~/components/TradeKindTag"
import ReturnTransactionsModal from "~/components/Transaction/ReturnTransactions"
import { HistoryColumns } from "~/components/Transaction/TransactionHistory"
import { TransactionModal } from "~/components/Transaction/TransactionModal"
import FilterTypes from "~/constants/FilterTypes"
import { TradeKinds } from "~/constants/Kinds"
import { PayableReceivableDate } from "~/constants/Payable"
import { Permission } from "~/constants/Permissions"
import { siteConfig } from "~/constants/config"
import { AppRoutes } from "~/constants/routes"
import { TRADE_KINDS } from "~/constants/static.items"
import {
  Order_By,
  SellerBifurcationType,
  useAuthenticatedUserQuery,
  useNetPayableReceiptAdvanceSummaryQuery,
  useNetPayableReceiptDetailQuery,
  useNetPayableReceiptListQuery,
  useNetPayableTradeAdvanceSummaryQuery,
  useNetPayableTradeDetailQuery,
  useNetPayableTradeListQuery,
  useNetPayableTradeOverduesQuery,
  useReceiptSellerAnalyticsQuery,
  useTradeSellerAnalyticsQuery,
  useUpdateReceiptNetPaymentMutation,
  useUpdateTradeNetPaymentMutation,
} from "~/generated/graphql"
import PayableActions from "~/pages/d/payable/advance/PayableActions"
import PayableFilter, {
  PayableFilterType,
} from "~/pages/d/payable/advance/PayableFilter"
import {
  PayableSelector,
  usePayableSelector,
} from "~/pages/d/payable/advance/PayableSelector"
import { AdvanceAdjustment } from "~/pages/d/payable/net/AdvanceAdjustment"
import { calculateFinalAmount } from "~/pages/pavti/lib/calculate-bifurcation-total"
import { inrFormatter } from "~/utils/inrFormatter"

const historyColumns = [
  HistoryColumns.DATE?.accessor,
  HistoryColumns.PAYMENT_OPT?.accessor,
  HistoryColumns.PAYMENT_MODE?.accessor,
  HistoryColumns.TRANSACTION_ID?.accessor,
  HistoryColumns.RELEASED_AMOUNT?.accessor,
  HistoryColumns.ACCOUNT_NO?.accessor,
  HistoryColumns.NARRATION?.accessor,
]
const OverdueInfo = ({ tradeId }: { tradeId: any }) => {
  const [opened, setOpened] = useState(false)
  const [{ data, fetching }] = useNetPayableTradeOverduesQuery({
    variables: { id: tradeId },
    pause: !opened || !tradeId,
  })

  return (
    <Tooltip
      content={
        <div className="text-sm p-1 whitespace-nowrap">
          {fetching || !data?.trade_by_pk?.summary ? (
            "Loading..."
          ) : (
            <>
              <div>
                Overdue Amount:{" "}
                <span className="font-700">
                  {inr(data?.trade_by_pk?.summary?.overdue_amount || 0)}
                </span>
              </div>
              <div>
                Overdue Days:{" "}
                <span className="font-700">
                  {data?.trade_by_pk?.summary?.overdue_days || 0}
                </span>
              </div>
            </>
          )}
        </div>
      }
    >
      <div
        className="flex items-center gap-1 cursor-pointer"
        onMouseEnter={() => setOpened(true)}
      >
        <Info size={20} weight="fill" className="text-blue-400" />
      </div>
    </Tooltip>
  )
}

const AdvanceAdjustmentInfo = ({
  tradeId,
  createdAt,
  supplierName,
  payableAmount,
  isTrade,
  tradeAmount,
}: {
  tradeId?: any
  createdAt?: string
  supplierName?: any
  payableAmount?: any
  isTrade?: boolean
  tradeAmount?: any
}) => {
  const [opened, setOpened] = useState(false)
  const [{ data: tradeData, fetching: tradeFetching }] =
    useNetPayableTradeAdvanceSummaryQuery({
      variables: { id: tradeId },
      pause: !opened || !tradeId || !isTrade,
    })
  const [{ data: receiptData, fetching: receiptFetching }] =
    useNetPayableReceiptAdvanceSummaryQuery({
      variables: { id: tradeId },
      pause: !opened || !tradeId || isTrade,
    })
  const summary = isTrade
    ? tradeData?.trade_by_pk?.advance_summary
    : receiptData?.receipt_by_pk?.advance_summary
  const fetching = isTrade ? tradeFetching : receiptFetching

  return (
    <Tooltip
      content={
        <AdvanceAdjustment
          tradeId={tradeId}
          createdAt={createdAt}
          supplierName={supplierName}
          payableAmount={summary?.seller_receivable_amount ?? payableAmount}
          releasedAmount={summary?.released_advance}
          totalAdvanceAvailable={summary?.requested_advance}
          adjustment={summary?.advance_used_in_current_trade}
          usedInOtherTrades={summary?.advance_used_in_other_trades}
          remaining={summary?.remaining_advance}
          isTrade={isTrade}
          loading={fetching || !summary}
          tradeAmount={tradeAmount}
        />
      }
    >
      <div
        className="flex items-center gap-1 cursor-pointer"
        onMouseEnter={() => setOpened(true)}
      >
        <Info size={17} className="cursor-pointer text-gray-600" />
      </div>
    </Tooltip>
  )
}

const NetPayable = () => {
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const page = Number(params.get("page")) || 1
  const id = params.get("id")
  const search = params.get("search")
  const [department] = useLocalStorage({ key: "department" })
  const { selected } = usePayableSelector()
  const isTrade = selected === "trade"
  const isShowImporter = useAuthorization(Permission.canUploadPayableReceivable)
  const canReturnTransaction = useAuthorization(
    Permission.CanReturnTransactions
  )

  const [returnId, setReturnId] = useState<any>(undefined)
  const canViewPayableReceivableReport = useAuthorization(
    Permission.ViewPayableReceivableReport
  )
  const [debouncedSearch] = useDebouncedValue(
    search?.trim(),
    siteConfig.debounce
  )
  const [{ data: authData }] = useAuthenticatedUserQuery()
  const me = authData?.me

  const [filter, setFilter] = useState<PayableFilterType>(() => {
    const savedFilter = localStorage.getItem(FilterTypes.netPayableFilter)
    return savedFilter ? JSON.parse(savedFilter) : {}
  })

  useEffect(() => {
    localStorage.setItem(FilterTypes.netPayableFilter, JSON.stringify(filter))
  }, [filter])

  const [{ data: tradeList, fetching: tradeFetching }, refetchTradeList] =
    useNetPayableTradeListQuery({
      variables: {
        limit: siteConfig.pageSize,
        offset: (page - 1) * siteConfig.pageSize,
        orderBy: {
          created_at: Order_By.DescNullsLast,
        },
        where: {
          current_status_id: {
            _neq: "Draft",
          },
          created_at: {
            _lte: filter?.date_to
              ? dayjs(filter?.date_to).endOf("d")
              : undefined,
            _gte: filter?.date_from
              ? dayjs(filter?.date_from).startOf("d")
              : undefined,
            _gt: dayjs(PayableReceivableDate).startOf("d"),
          },
          ...(filter?.placed_by
            ? {
                created_by_id: {
                  _eq: filter?.placed_by?.id?.toString(),
                },
              }
            : undefined),
          ...(isTrade
            ? {
                receipt_id: {
                  _is_null: true,
                },
                kind: {
                  _in: ["final", "inward"],
                  _eq: filter?.patti_type,
                },
                department: {
                  reference_id: {
                    _eq:
                      (department && String(department)) ||
                      me?.agent?.current_department?.codename,
                  },
                },
                ...(debouncedSearch
                  ? {
                      id:
                        debouncedSearch && Number(debouncedSearch)
                          ? {
                              _eq: Number(debouncedSearch),
                            }
                          : undefined,
                    }
                  : undefined),

                seller:
                  (debouncedSearch && !Number(debouncedSearch)) ||
                  filter?.seller
                    ? {
                        name:
                          debouncedSearch && !Number(debouncedSearch)
                            ? {
                                _ilike: `%${debouncedSearch}%`,
                              }
                            : undefined,
                        reference_id: filter?.seller
                          ? {
                              _eq: filter?.seller?.codename,
                            }
                          : undefined,
                      }
                    : undefined,
              }
            : undefined),
          _or: [
            {
              parent_trade_id: {
                _is_null: true,
              },
            },
            {
              parent_trade: {
                kind: {
                  _nin: ["inward"],
                },
                _or: [
                  {
                    parent_trade_id: {
                      _is_null: true,
                    },
                  },
                  {
                    parent_trade: {
                      kind: {
                        _nin: ["inward"],
                      },
                    },
                  },
                ],
              },
            },
          ],
        },
      },
      pause: !isTrade,
    })

  const [{ data: receiptList, fetching: receiptFetching }, refetchReceiptList] =
    useNetPayableReceiptListQuery({
      variables: {
        limit: siteConfig.pageSize,
        offset: (page - 1) * siteConfig.pageSize,
        orderBy: {
          created_at: Order_By.DescNullsLast,
        },
        where: {
          created_at: {
            _lte: filter?.date_to
              ? dayjs(filter?.date_to).endOf("d")
              : undefined,
            _gte: filter?.date_from
              ? dayjs(filter?.date_from).startOf("d")
              : undefined,
            _gt: dayjs(PayableReceivableDate).startOf("d"),
          },
          ...(filter?.placed_by
            ? {
                seller: {
                  id: {
                    _eq: filter?.placed_by?.id?.toString(),
                  },
                },
              }
            : undefined),
          ...(!isTrade
            ? {
                kind: {
                  _in: ["final"],
                  _eq: filter?.patti_type,
                },
                department: {
                  reference_id: {
                    _eq:
                      (department && String(department)) ||
                      me?.agent?.current_department?.codename,
                  },
                },
                ...(debouncedSearch
                  ? {
                      id:
                        debouncedSearch && Number(debouncedSearch)
                          ? {
                              _eq: Number(debouncedSearch),
                            }
                          : undefined,
                    }
                  : undefined),

                seller:
                  (debouncedSearch && !Number(debouncedSearch)) ||
                  filter?.seller
                    ? {
                        name:
                          debouncedSearch && !Number(debouncedSearch)
                            ? {
                                _ilike: `%${debouncedSearch}%`,
                              }
                            : undefined,
                        reference_id: filter?.seller
                          ? {
                              _eq: filter?.seller?.codename,
                            }
                          : undefined,
                      }
                    : undefined,
              }
            : undefined),
        },
      },
      pause: isTrade,
    })

  const [{ data: tradeDetailData, fetching: tradeDetailFetching }] =
    useNetPayableTradeDetailQuery({
      variables: {
        id: id,
      },
      pause: !id || !isTrade,
      requestPolicy: "network-only",
    })
  const [{ data: receiptDetailData, fetching: receiptDetailFetching }] =
    useNetPayableReceiptDetailQuery({
      variables: {
        id: id,
      },
      pause: !id || isTrade,
      requestPolicy: "network-only",
    })
  const totalRows = isTrade
    ? tradeList?.trade_aggregate?.aggregate?.count || 0
    : receiptList?.receipt_aggregate?.aggregate?.count || 0

  const totalCredit = tradeList?.trade?.reduce(
    (acc, trade) => acc + (Number(trade?.creditNote?.[0]?.amount) || 0),
    0
  )
  const totalDebit = tradeList?.trade?.reduce(
    (acc, trade) => acc + (Number(trade?.debitNote?.[0]?.amount) || 0),
    0
  )

  const [
    {
      data: tradeSellerAnalyticsData,
      fetching: tradeSellerAnalyticsDataLoading,
    },
  ] = useTradeSellerAnalyticsQuery({
    pause: !isTrade,
    variables: {
      departments:
        (department && String(department)) ||
        me?.agent?.current_department?.codename,

      createdFrom: filter.date_from
        ? dayjs(filter.date_from).format("YYYY-MM-DD")
        : dayjs(PayableReceivableDate).format("YYYY-MM-DD"),
      createdTill: filter.date_to
        ? dayjs(filter.date_to).format("YYYY-MM-DD")
        : undefined,
      sellerReferenceId: filter.seller ? filter.seller?.codename : undefined,
      createdById: filter.placed_by?.id ? filter.placed_by?.id : undefined,
      tradeKinds: filter.patti_type
        ? [filter.patti_type]
        : [TradeKinds.HishobPatti, TradeKinds.InwardPatti],
      search: debouncedSearch,
    },
  })

  const [
    {
      data: receiptSellerAnalyticsData,
      fetching: receiptSellerAnalyticsDataLoading,
    },
  ] = useReceiptSellerAnalyticsQuery({
    pause: isTrade,
    variables: {
      departments:
        (department && String(department)) ||
        me?.agent?.current_department?.codename,

      createdFrom: filter.date_from
        ? dayjs(filter.date_from).format("YYYY-MM-DD")
        : dayjs(PayableReceivableDate).format("YYYY-MM-DD"),
      createdTill: filter.date_to
        ? dayjs(filter.date_to).format("YYYY-MM-DD")
        : undefined,
      sellerReferenceId: filter.seller ? filter.seller?.codename : undefined,
      createdById: filter.placed_by?.id ? filter.placed_by?.id : undefined,
      tradeKinds: filter.patti_type
        ? [filter.patti_type]
        : [TradeKinds.HishobPatti, TradeKinds.InwardPatti],
      search: debouncedSearch,
    },
  })

  const COLUMNS = [
    {
      Header: "Created On",
      accessor: "created_at",
      Cell({ value }) {
        // if (!value) return "--"
        return (
          <Cell
            title={dayjs(value).format("D MMM 'YY")}
            subtitle={dayjs(value).format("hh:mm a")}
          />
        )
      },
      formatter: () => {
        return "Total"
      },
    },
    {
      Header: <div className="pl-3">{isTrade ? "Trade Id" : "Receipt Id"}</div>,
      id: "id",
      Cell({ row: { original } }) {
        const value = isTrade ? original?.id : original?.id
        if (!value) return "--"
        const remaining = Number(
          Number(original?.seller_bifurcation?.total).toFixed(2)
        )
        const isSettled = !remaining || remaining === 0 || remaining < 0
        return isSettled ? (
          <div className="pl-4">
            <div
              className="link-text text-left w-fit cursor-pointer"
              onClick={() => {
                params.set("transaction", "true")
                params.set("id", original?.id)
                setParams(params)
              }}
            >
              #{value}
            </div>
          </div>
        ) : (
          <div className="pl-4">#{value}</div>
        )
      },
    },
    {
      Header: "Type",
      id: "kind",
      Cell({ row: { original } }) {
        const value = isTrade ? original?.kind : original?.kind
        const kind = TRADE_KINDS.find(k => k.id === value)
        if (!kind?.name) return "--"

        return <TradeKindTag kind={kind?.id as any} />
      },
    },
    {
      Header: "Supplier",
      accessor: "seller.user[0]",
      id: "seller",
      Cell({ row: { original } }) {
        const value = isTrade
          ? original?.seller?.user?.[0]
          : original?.seller?.user?.[0]
        if (!value) return "--"
        return (
          <Cell
            title={value.full_name}
            subtitle={
              <CodenameTag referenceId={value.external_reference_id2} />
            }
          />
        )
      },
    },
    {
      Header: "Total Amount",
      accessor: "seller_bifurcation",
      Cell({ value, row: { original } }) {
        if (!value) return "--"
        const base = original?.seller_bifurcation?.deducted_amount?.val
        const gst = original?.seller_bifurcation?.seller_tax_amount?.val
        const expense = original?.seller_bifurcation?.seller_expense_amount?.val
        const expense_gst =
          original?.seller_bifurcation?.seller_expense_tax_amount?.val
        const supplier_charge =
          original?.seller_bifurcation?.supplier_charge?.val
        const advanceAmount = original?.seller_bifurcation?.advance_amount?.val

        const total = calculateFinalAmount({
          ...pick(
            value,
            "deducted_amount",
            "seller_tax_amount",
            "seller_expense_amount",
            "seller_expense_tax_amount",
            "supplier_charge"
          ),
        })

        return (
          <Cell
            titleClassName="font-700 !text-green"
            title={
              <Tooltip
                content={`Base: ${inr(base)} | GST: ${inr(
                  gst
                )} | Expense: ${inr(expense)} | Expense GST: ${inr(
                  expense_gst
                )} | Supplier Charge: ${inr(
                  supplier_charge
                )} | Advance Amount: ${inr(advanceAmount)}`}
              >
                <div>{inr(total)}</div>
              </Tooltip>
            }
            subtitle={gst ? "Incl. of taxes" : null}
          />
        )
      },
      formatter: () => {
        const totalInvoiceValue = isTrade
          ? tradeList?.trade
              ?.map(
                (trade: any) =>
                  Number(trade?.seller_bifurcation?.deducted_amount?.val) -
                  Number(
                    trade?.seller_bifurcation?.seller_expense_amount?.val
                  ) -
                  Number(
                    trade?.seller_bifurcation?.seller_expense_tax_amount?.val
                  ) -
                  Number(trade?.seller_bifurcation?.supplier_charge?.val) +
                  Number(trade?.seller_bifurcation?.seller_tax_amount?.val)
              )
              ?.reduce((a: any, b: any) => a + b, 0) || 0
          : receiptList?.receipt
              ?.map(
                (receipt: any) =>
                  Number(receipt?.seller_bifurcation?.deducted_amount?.val) -
                  Number(
                    receipt?.seller_bifurcation?.seller_expense_amount?.val
                  ) -
                  Number(
                    receipt?.seller_bifurcation?.seller_expense_tax_amount?.val
                  ) -
                  Number(receipt?.seller_bifurcation?.supplier_charge?.val) +
                  Number(receipt?.seller_bifurcation?.seller_tax_amount?.val)
              )
              ?.reduce((a: any, b: any) => a + b, 0) || 0
        return inrFormatter.format(totalInvoiceValue)
      },
    },
    {
      Header: "Credit Note",
      accessor: "seller_bifurcation.seller_credit_note_amount",
      id: "creditNote",
      Cell({ value }) {
        if (!value) return "--"
        return (
          <Cell
            titleClassName="font-700 !text-green"
            title={`${value?.op + inr(value?.val)}`}
          />
        )
      },
      formatter: () => {
        return inrFormatter.format(totalCredit || 0)
      },
    },
    {
      Header: "Debit Note",
      accessor: "seller_bifurcation.seller_debit_note_amount",
      id: "debitNote",
      Cell({ value }) {
        if (!value) return "--"
        return (
          <Cell
            titleClassName="font-700 !text-red"
            title={`${value?.op + inr(value?.val)}`}
          />
        )
      },
      formatter: () => {
        return inrFormatter.format(totalDebit || 0)
      },
    },
    {
      Header: "Advance Amount",
      accessor: "advance_payments_aggregate.aggregate.sum.amount",
      Cell({ value }) {
        if (!value) return "--"
        return <Cell titleClassName="font-700 !text-green" title={inr(value)} />
      },
      aggregate: "sum",
      formatter: inrFormatter.format,
    },
    {
      Header: "Paid Amount",
      accessor: "seller_bifurcation.paid_amount.val",
      Cell({ value, row: { original } }) {
        const utilized = original?.seller_bifurcation.advance_amount.val
        const total = calculateFinalAmount(
          pick(
            original?.seller_bifurcation,
            "deducted_amount",
            "seller_tax_amount",
            "seller_expense_amount",
            "seller_expense_tax_amount",
            "supplier_charge"
          )
        )
        return (
          <Cell
            titleClassName="font-700 !text-green"
            title={inr(value)}
            subtitle={
              utilized ? (
                <div className="text-gray-600 flex gap-1 items-start">
                  <AdvanceAdjustmentInfo
                    tradeId={original?.id}
                    createdAt={dayjs(original?.created_at).format(
                      "DD MMM 'YYYY"
                    )}
                    supplierName={original?.seller?.user?.[0]?.full_name || "—"}
                    payableAmount={
                      original?.seller_bifurcation?.total?.val || 0
                    }
                    tradeAmount={total}
                    isTrade={isTrade}
                  />
                  <p className="inline mb-2 text-xs">
                    Adv. Utilized
                    <br />
                    {inr(utilized)}
                  </p>
                </div>
              ) : undefined
            }
          />
        )
      },
      aggregate: "sum",
      formatter: inrFormatter.format,
    },
    {
      Header: "TDS Amount",
      accessor: "seller_bifurcation.seller_tds.val",
      Cell({ value }) {
        // if (!value) return "--"
        return <Cell titleClassName="font-700 !text-red" title={inr(value)} />
      },
      aggregate: "sum",
      formatter: inrFormatter.format,
    },
    {
      Header: "Payable Amount",
      accessor: "seller_bifurcation.total",
      Cell({ value }) {
        // if (!value) return "--"
        return <Cell titleClassName="font-700 !text-red" title={inr(value)} />
      },
      aggregate: "sum",
      formatter: inrFormatter.format,
    },
    {
      Header: "Placed By",
      accessor: "created_by[0]",
      Cell({ value, row }) {
        if (!value) return "--"
        return (
          <Tooltip
            content={
              <div>
                <p>{value?.full_name || "na"}</p>
                <p className="text-gray-400">
                  {dayjs(row?.original?.created_at).format(
                    "DD MMM 'YY | h:mm a"
                  )}
                </p>
              </div>
            }
          >
            <Avatar
              className="relative text-center"
              name={value?.full_name}
              src={value?.photo?.url}
            />
          </Tooltip>
        )
      },
    },
    {
      Header: "Transaction Status",
      accessor: "transactions",
      Cell({ row: { original }, value }) {
        const remaining = Number(
          Number(original?.seller_bifurcation?.total).toFixed(2)
        )
        const paid = original?.seller_bifurcation?.paid_amount?.val
        const isSettled = remaining === 0 || remaining < 0
        return (
          <Cell
            titleClassName={classNames(
              "flex gap-1 items-center font-600 ",
              isSettled ? "!text-green" : paid ? "!text-blue" : "!text-yellow"
            )}
            title={
              <>
                <Circle weight="fill" />
                {isSettled ? "Settled" : paid ? "Partial" : "Pending"}
              </>
            }
          />
        )
      },
    },
    {
      Header: "Last Transaction",
      accessor: "transactions.0.date",
      Cell({ value }) {
        if (!value) return "--"
        return <Cell title={<>{dayjs(value).format("DD MMM 'YYYY")}</>} />
      },
    },
    {
      Header: "Remark",
      accessor: "remark",
      Cell({ value, row: { original } }) {
        const parentReceiptId = original?.transactions?.[0]
          ?.parent_transaction_id
          ? original?.transactions?.[0]?.trade?.receipt_id
          : undefined
        return (
          <>{parentReceiptId ? `Settled Receipt #${parentReceiptId}` : value}</>
        )
      },
    },
    {
      Header: "",
      accessor: "updated_at",
      Cell({ row: { original } }) {
        const [params, setParams] = useSearchParams()

        return (
          <div className="flex items-center gap-1">
            {isTrade && <OverdueInfo tradeId={original.id} />}
            <Popover
              minimal
              arrow={false}
              className="p-0"
              trigger="click"
              interactive
              placement="bottom"
              content={
                <Menu className="max-h-72">
                  <MenuItem
                    onClick={() => {
                      params.set("remark", original.id)
                      setParams(params)
                    }}
                  >
                    Remark
                  </MenuItem>
                  <MenuItem
                    hidden={!isTrade || !canReturnTransaction}
                    onClick={() => {
                      setReturnId(original.id)
                    }}
                  >
                    Return Transactions
                  </MenuItem>
                </Menu>
              }
            >
              <button className="hover:bg-gray-300 rounded">
                <DotsThreeVertical size={20} />
              </button>
            </Popover>
          </div>
        )
      },
    },
  ]

  const analyticsData = isTrade
    ? tradeSellerAnalyticsData?.aggregate_trade_seller_summary
    : receiptSellerAnalyticsData?.aggregate_receipt_seller_summary

  const summaryCards = [
    {
      id: "total",
      title: "Total Amount",
      value: Number(
        Number(
          calculateFinalAmount(
            pick(
              analyticsData as any,
              "deducted_amount",
              "seller_tax_amount",
              "seller_expense_amount",
              "seller_expense_tax_amount",
              "supplier_charge"
            )
          ) as any
        ).toFixed(2)
      ),
      helper: "",
      intentClass: "text-blue-400",
      iconBgClass: "bg-blue-100",
      icon: Receipt,
    },
    {
      id: "advance",
      title: "Remaining Advance Amount",
      value: Number(
        Number((analyticsData as any)?.advance_remaining_amount || 0)?.toFixed(
          2
        )
      ),
      intentClass: "text-yellow-600",
      iconBgClass: "bg-yellow-100",
      icon: Handshake,
    },
    {
      id: "paid",
      title: "Paid Amount",
      value: Number(Number(analyticsData?.paid_amount?.val as any)?.toFixed(2)),
      intentClass: "text-green-300",
      iconBgClass: "bg-green-100",
      icon: CurrencyInr,
    },
    {
      id: "payable",
      title: "Payable Amount",
      value: Number(Number(Number(analyticsData?.total as any)?.toFixed(2))),
      intentClass: "text-red-500",
      iconBgClass: "bg-red-100",
      valueClassName: "!text-red-500",
      icon: TrendDown,
    },
  ]
  return (
    <>
      <header className="grid grid-cols-3 p-2 border-b-1 border-b-gray-300">
        <SegmentedControl
          value={"net"}
          onChange={value => {
            if (value === "advance") {
              navigate(AppRoutes.advancePayable)
            }
            if (value === "settlement") {
              navigate(AppRoutes.payableSettlement)
            }
          }}
          data={[
            {
              label: "Payable Settlement",
              value: "settlement",
              icon: <Receipt className="mr-1" />,
            },
            {
              label: "Advance",
              value: "advance",
              icon: <List className="mr-1" />,
            },
            {
              label: "Net Payable",
              value: "net",
              icon: <Receipt className="mr-1" />,
            },
          ].filter(b => b)}
        />
        <div className="flex items-center gap-3">
          <InputGroup
            className={"w-full"}
            leftElement={<MagnifyingGlass />}
            inputProps={{
              placeholder: "Search by Trade id, Seller name ",
              value: search || "",
              onChange(e) {
                params.set("search", e.target.value)
                params.delete("page")
                setParams(params)
              },
            }}
          />
          <PayableFilter
            filter={filter}
            setFilter={setFilter}
            hideAdvanceStatus
            hideAdvanceType
            hideTransactionStatus
            hideBookingPatti
            hideSaudaPavti
            hideStoragePatti
            hideTradeKind={!isTrade}
            hideDepartment
          />
        </div>
        <div className="flex gap-2 justify-end">
          {isShowImporter || canViewPayableReceivableReport ? (
            <PayableActions />
          ) : null}
          <PayableSelector />
        </div>
      </header>
      <section className="m-4 bg-white p-4 mb-0 rounded-t-xl">
        <div className="grid grid-cols-4 gap-4">
          <TableSummary
            summary={summaryCards}
            loading={
              tradeSellerAnalyticsDataLoading ||
              receiptSellerAnalyticsDataLoading
            }
          />
        </div>
      </section>

      <RemarkModal
        refetch={isTrade ? refetchTradeList : refetchReceiptList}
        title="Remark"
        useAddRemarkMutation={
          isTrade
            ? useUpdateTradeNetPaymentMutation
            : useUpdateReceiptNetPaymentMutation
        }
      />
      <TransactionModal
        refetch={isTrade ? refetchTradeList : refetchReceiptList}
        transactions={
          isTrade
            ? tradeDetailData?.trade_by_pk?.transactions
            : receiptDetailData?.receipt_by_pk?.transactions
        }
        canAddTransaction={false}
        columns={historyColumns}
        sellerBifurcation={
          tradeDetailData?.trade_by_pk
            ?.seller_bifurcation as SellerBifurcationType
        }
        fetching={isTrade ? tradeDetailFetching : receiptDetailFetching}
        createdAt={
          isTrade
            ? tradeDetailData?.trade_by_pk?.created_at
            : receiptDetailData?.receipt_by_pk?.created_at
        }
        idLabel={isTrade ? "Trade Id" : "Receipt Id"}
        objectId={
          isTrade
            ? tradeDetailData?.trade_by_pk?.id
            : receiptDetailData?.receipt_by_pk?.id
        }
        historyCols={`!grid-cols-7`}
        historyColSpan={`!col-span-5`}
        tradeId={isTrade ? tradeDetailData?.trade_by_pk?.id : undefined}
        receiptId={isTrade ? undefined : receiptDetailData?.receipt_by_pk?.id}
        bankSelectorOptions={
          isTrade
            ? tradeDetailData?.trade_by_pk?.seller?.user?.[0]?.payment_details
                ?.length !== 0
              ? tradeDetailData?.trade_by_pk?.seller?.user?.[0]
                  ?.payment_details || []
              : tradeDetailData?.trade_by_pk?.seller?.user?.[0]
                  ?.my_businesses?.[0]?.payment_details || []
            : receiptDetailData?.receipt_by_pk?.seller?.user?.[0]
                ?.payment_details?.length !== 0
            ? receiptDetailData?.receipt_by_pk?.seller?.user?.[0]
                ?.payment_details || []
            : receiptDetailData?.receipt_by_pk?.seller?.user?.[0]
                ?.my_businesses?.[0]?.payment_details || []
        }
        creditNote={tradeDetailData?.trade_by_pk?.creditNote}
        debitNote={tradeDetailData?.trade_by_pk?.debitNote}
        issuanceNotePendingVerification={
          tradeDetailData?.trade_by_pk?.pending_issuance_note_bool?.length
            ? tradeDetailData?.trade_by_pk?.pending_issuance_note_bool?.length >
              0
            : false
        }
      />
      <TableView
        relative={false}
        className="!mt-0 overflow-x-auto"
        tableClassName="!mt-0 !min-h-[600px] max-h-[600px] overflow-y-scroll"
        paginationProps={{
          total: Math.ceil(totalRows / siteConfig.pageSize),
          page: page,
          onChange(page) {
            params.set("page", page.toString())
            params.set("search", "")
            setParams(params)
          },
        }}
        isLoading={isTrade ? tradeFetching : receiptFetching}
        columns={COLUMNS}
        data={isTrade ? tradeList?.trade || [] : receiptList?.receipt || []}
        meta={<>{totalRows} results</>}
        aggregateColumnClassName={
          "bg-blue-100 hover:bg-blue-100 p-10 sticky bottom-0 z-[8] font-600"
        }
      />

      <ReturnTransactionsModal
        isOpen={!!returnId}
        tradeId={isTrade ? returnId : undefined}
        receiptId={!isTrade ? returnId : undefined}
        onClose={async refetch => {
          setReturnId(undefined)
          if (!!refetch && isTrade) {
            await refetchTradeList?.({
              requestPolicy: "network-only",
            })
          }
          if (!!refetch && !isTrade) {
            await refetchReceiptList?.({
              requestPolicy: "network-only",
            })
          }
        }}
      />
    </>
  )
}

export default NetPayable
